import { useEffect, useRef } from 'react'
import { getConfig } from '../config/constants'

interface GoogleLoginButtonProps {
  onSuccess: (credential: string) => void
  onError: (error: any) => void
  text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin'
  theme?: 'outline' | 'filled_blue' | 'filled_black'
  size?: 'large' | 'medium' | 'small'
  width?: string
  disabled?: boolean
}

interface GoogleIdentityServices {
  accounts: {
    id: {
      initialize: (config: any) => void
      renderButton: (element: HTMLElement, config: any) => void
    }
  }
}

declare global {
  interface Window {
    google: GoogleIdentityServices
  }
}

export default function GoogleLoginButton({
  onSuccess,
  onError,
  text = 'signin_with',
  theme = 'outline',
  size = 'large',
  width = '400',
  disabled = false
}: GoogleLoginButtonProps) {
  const buttonRef = useRef<HTMLDivElement>(null)
  const initialized = useRef(false)

  useEffect(() => {
    const initializeGoogleLogin = () => {
      if (!window.google || !buttonRef.current || initialized.current) {
        return
      }

      try {
        const config = getConfig();
        const clientId = config.GOOGLE_CLIENT_ID;
        
        console.log('🔧 Iniciando Google Identity Services...');
        console.log('📱 Google Client ID:', clientId);
        console.log('🌐 Config completa:', config);
        
        if (!clientId) {
          const errorMsg = 'Google Client ID não está disponível!';
          console.error('❌', errorMsg);
          onError(new Error(errorMsg));
          return;
        }

        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: (response: any) => {
            if (response.credential) {
              onSuccess(response.credential)
            } else {
              onError(new Error('No credential received'))
            }
          },
          auto_select: false,
          cancel_on_tap_outside: true
        })

        window.google.accounts.id.renderButton(buttonRef.current, {
          type: 'standard',
          theme,
          size,
          text,
          width,
          logo_alignment: 'left'
        })

        initialized.current = true
      } catch (error) {
        console.error('Error initializing Google login:', error)
        onError(error)
      }
    }

    // Verificar se o script já foi carregado
    if (window.google) {
      initializeGoogleLogin()
    } else {
      // Carregar script do Google Identity Services
      const script = document.createElement('script')
      script.src = 'https://accounts.google.com/gsi/client'
      script.async = true
      script.defer = true
      script.onload = initializeGoogleLogin
      script.onerror = () => onError(new Error('Failed to load Google Identity Services'))
      
      document.head.appendChild(script)

      return () => {
        document.head.removeChild(script)
      }
    }
  }, [onSuccess, onError, text, theme, size, width])

  if (disabled) {
    return (
      <div className="w-full">
        <button
          disabled
          className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-400 bg-gray-100 cursor-not-allowed"
        >
          <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
            <path
              fill="currentColor"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="currentColor"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="currentColor"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="currentColor"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          Entrar com Google
        </button>
      </div>
    )
  }

  return (
    <div className="w-full flex justify-center">
      <div ref={buttonRef} />
    </div>
  )
} 